/**
 * Thin wrapper around Google Analytics 4 (gtag.js). The script tag itself is
 * injected from `index.html` so the snippet is loaded as soon as possible
 * without waiting for our React bundle to parse.
 *
 * We always fall back gracefully if gtag is not available (e.g. ad-blocked,
 * dev tools privacy mode, SSR). All event helpers below are best-effort.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const MEASUREMENT_ID = 'G-WWZPRS8LVJ';
const ADS_ID = 'AW-18191034379';
const ADS_PURCHASE_LABEL = 'AW-18191034379/9nTRCJer2rMcEIvQlOJD';

function gtag(...args: unknown[]): void {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag === 'function') {
    window.gtag(...args);
    return;
  }
  // The page may render before gtag.js finishes loading. Fall back to the
  // dataLayer queue so events are not lost.
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(args);
}

/**
 * Fire a synthetic `page_view` after a client-side route change. Mirrors
 * the GA4 default page_view payload so reports treat SPA navigations as
 * regular page views.
 */
export function trackPageView(path: string, title?: string): void {
  gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title ?? document.title,
    send_to: MEASUREMENT_ID,
  });
}

/**
 * Set or clear the GA `user_id`. Called on login / verify (authenticated)
 * and again on logout (with `null`).
 */
export function setUserId(userId: string | null): void {
  gtag('config', MEASUREMENT_ID, { user_id: userId ?? undefined });
}

// ----- Conversion helpers --------------------------------------------------

/** Form-level intent (the user pressed "Send magic link" on /signup or /login). */
export function trackMagicLinkRequested(intent: 'signup' | 'login'): void {
  gtag('event', intent === 'signup' ? 'sign_up_request' : 'login_request', {
    method: 'magic_link',
    intent,
  });
}

/**
 * Magic-link verify success. `isNewUser=true` becomes a GA `sign_up`
 * conversion (a default GA4 recommended event), otherwise a `login` event.
 * For new users, also fires a Google Ads conversion.
 */
export function trackMagicLinkVerified(isNewUser: boolean): void {
  gtag('event', isNewUser ? 'sign_up' : 'login', {
    method: 'magic_link',
  });

  if (isNewUser) {
    // Google Ads sign-up conversion. Append `/<CONVERSION_LABEL>` to send_to
    // once you create a "Sign-up" conversion action in Google Ads.
    gtag('event', 'conversion', {
      send_to: 'AW-18191034379/1cLNCMb3srQcEIvQlOJD',
    });
  }
}

interface EventItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  price?: number;
  quantity?: number;
  currency?: string;
}

/** Public event page viewed. Maps to GA4 `view_item`. */
export function trackViewEvent(args: {
  eventId: string;
  eventName: string;
  orgSlug?: string;
}): void {
  const item: EventItem = {
    item_id: args.eventId,
    item_name: args.eventName,
    item_category: args.orgSlug,
  };
  gtag('event', 'view_item', { items: [item] });
}

/** Buyer reached the checkout page. Maps to GA4 `begin_checkout`. */
export function trackBeginCheckout(args: {
  eventId: string;
  eventName: string;
  currency: string;
  valueCents: number;
  ticketTypeId?: string;
  quantity?: number;
}): void {
  gtag('event', 'begin_checkout', {
    currency: args.currency.toUpperCase(),
    value: args.valueCents / 100,
    items: [
      {
        item_id: args.ticketTypeId ?? args.eventId,
        item_name: args.eventName,
        price: args.valueCents / 100,
        quantity: args.quantity ?? 1,
        currency: args.currency.toUpperCase(),
      },
    ],
  });
}

/** Order was paid. Maps to GA4 `purchase` (event ticket sale — not a Boletra revenue event). */
export function trackPurchase(args: {
  orderId: string;
  eventName: string;
  currency: string;
  totalCents: number;
  taxCents?: number;
  feeCents?: number;
  items?: EventItem[];
}): void {
  const value = args.totalCents / 100;
  const currency = args.currency.toUpperCase();

  gtag('event', 'purchase', {
    transaction_id: args.orderId,
    value,
    currency,
    tax: args.taxCents ? args.taxCents / 100 : undefined,
    shipping: args.feeCents ? args.feeCents / 100 : undefined,
    items: args.items ?? [{ item_id: 'event', item_name: args.eventName }],
  });
}

/**
 * Promotor compró un paquete de créditos — este ES un ingreso de Boletra.
 * Dispara GA4 `purchase` + conversión de Google Ads.
 * `valueCents` debe estar en USD (precio canónico de los paquetes).
 */
export function trackCreditsPurchase(args: {
  packageId: string;
  credits: number;
  totalUsdCents: number;
}): void {
  const value = args.totalUsdCents / 100;

  gtag('event', 'purchase', {
    transaction_id: `credits_${args.packageId}_${Date.now()}`,
    value,
    currency: 'USD',
    items: [
      { item_id: args.packageId, item_name: `Credits ${args.credits}`, quantity: 1, price: value },
    ],
  });

  gtag('event', 'conversion', {
    send_to: ADS_PURCHASE_LABEL,
    value,
    currency: 'USD',
  });
}

/**
 * Generic engagement event hook. Use sparingly for things that are not
 * already covered by the helpers above (e.g. clicking a particular CTA).
 */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  gtag('event', name, params ?? {});
}
